import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCaretDown, faCaretUp, faCopy, faEdit, faUserPlus, faUserSlash } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { BFormCheckbox } from "bootstrap-vue";
import { computed, nextTick, reactive, ref, watch } from "vue";
import { getGalaxyInstance } from "@/app";
import { useToast } from "@/composables/toast";
import { getAppRoot } from "@/onload/loadConfig";
import { errorMessageAsString } from "@/utils/simple-error";
import { getFullAppUrl } from "@/utils/utils";
import EditableUrl from "./EditableUrl.vue";
import WorkflowEmbed from "./Embeds/WorkflowEmbed.vue";
import ErrorMessages from "./ErrorMessages.vue";
import UserSharing from "./UserSharing.vue";
import Heading from "@/components/Common/Heading.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'SharingPage',
    props: {
        id: null,
        pluralName: null,
        modelClass: null
    },
    setup(__props) {
        const props = __props;
        library.add(faCopy, faEdit, faUserPlus, faUserSlash, faCaretDown, faCaretUp);
        const errors = ref([]);
        function addError(newError) {
            // temporary turning Set into Array, until we update till Vue 3.0, that supports Set reactivity
            errors.value = Array.from(new Set(errors.value).add(newError));
        }
        function onErrorDismissed(index) {
            errors.value.splice(index, 1);
        }
        const defaultExtra = () => ({
            can_change: [],
            cannot_change: [],
        });
        const item = ref({
            title: "title",
            username_and_slug: "__username__/__slug__",
            importable: false,
            published: false,
            users_shared_with: [],
            extra: defaultExtra(),
        });
        const itemUrl = reactive({
            prefix: "",
            slug: "",
        });
        watch(() => item.value.username_and_slug, (value) => {
            if (value) {
                const index = value.lastIndexOf("/");
                itemUrl.prefix = getFullAppUrl(value.substring(0, index + 1));
                itemUrl.slug = value.substring(index + 1);
            }
        }, { immediate: true });
        const slugUrl = computed(() => `${getAppRoot()}api/${props.pluralName.toLowerCase()}/${props.id}/slug`);
        function onChangeSlug(newValue) {
            itemUrl.slug = newValue;
        }
        async function onSubmitSlug(newValue) {
            itemUrl.slug = newValue;
            try {
                await axios.put(slugUrl.value, { new_slug: newValue });
            }
            catch (e) {
                onError(e);
            }
        }
        const itemStatus = computed(() => (item.value.published ? "accessible via link and published" : "accessible via link"));
        const publishedUrl = computed(() => `${getAppRoot()}${props.pluralName.toLocaleLowerCase()}/list_published`);
        function onError(axiosError) {
            addError(errorMessageAsString(axiosError));
        }
        const ready = ref(false);
        async function getSharing() {
            ready.value = false;
            try {
                const response = await axios.get(`${getAppRoot()}api/${props.pluralName.toLocaleLowerCase()}/${props.id}/sharing`);
                assignItem(response.data, true);
            }
            catch (e) {
                onError(e);
            }
        }
        getSharing();
        function permissionsChangeRequired(data) {
            if (data.extra) {
                return data.extra.can_change.length > 0 || data.extra.cannot_change.length > 0;
            }
            else {
                return false;
            }
        }
        const actions = {
            enableLinkAccess: "enable_link_access",
            disableLinkAccess: "disable_link_access",
            publish: "publish",
            unpublish: "unpublish",
            share_with: "share_with_users",
        };
        const { success } = useToast();
        async function setSharing(action, userId, shareOption) {
            let userIds;
            if (Array.isArray(userId)) {
                userIds = userId;
            }
            else {
                userIds = userId ? userId.replace(/ /g, "").split(",") : undefined;
            }
            const data = {
                user_ids: userIds,
                share_option: shareOption ? shareOption : undefined,
            };
            try {
                const response = await axios.put(`${getAppRoot()}api/${props.pluralName.toLocaleLowerCase()}/${props.id}/${action}`, data);
                errors.value = [];
                const userIdsSaved = userIds && !permissionsChangeRequired(response.data) && response.data.errors.length === 0;
                assignItem(response.data, userIdsSaved ?? false);
                if (!permissionsChangeRequired(response.data)) {
                    success("Sharing preferences saved");
                }
            }
            catch (e) {
                onError(e);
            }
        }
        const userSharing = ref();
        async function assignItem(newItem, overwriteCandidates) {
            if (newItem.errors) {
                errors.value = newItem.errors;
            }
            item.value = newItem;
            if ((!item.value.extra || newItem.errors?.length) ?? 0 > 0) {
                item.value.extra = defaultExtra();
            }
            if (overwriteCandidates) {
                await nextTick();
                //@ts-ignore incorrect property not found error
                userSharing.value?.reset();
            }
            ready.value = true;
        }
        function onImportable(importable) {
            if (importable) {
                setSharing(actions.enableLinkAccess);
            }
            else {
                item.value.published = false;
                setSharing(actions.disableLinkAccess);
            }
        }
        function onPublish(published) {
            if (published) {
                item.value.importable = true;
                setSharing(actions.publish);
            }
            else {
                setSharing(actions.unpublish);
            }
        }
        const hasUsername = ref(Boolean(getGalaxyInstance().user.get("username")));
        const newUsername = ref("");
        const slugSet = computed(() => itemUrl.slug != "__slug__" && itemUrl.prefix != "__username__");
        async function setUsername() {
            axios
                .put(`${getAppRoot()}api/users/${getGalaxyInstance().user.id}/information/inputs`, {
                username: newUsername.value || "",
            })
                .then((response) => {
                hasUsername.value = true;
                getSharing();
            })
                .catch(onError);
        }
        const embedable = computed(() => item.value.importable && props.modelClass.toLocaleLowerCase() === "workflow");
        return { __sfc: true, props, errors, addError, onErrorDismissed, defaultExtra, item, itemUrl, slugUrl, onChangeSlug, onSubmitSlug, itemStatus, publishedUrl, onError, ready, getSharing, permissionsChangeRequired, actions, success, setSharing, userSharing, assignItem, onImportable, onPublish, hasUsername, newUsername, slugSet, setUsername, embedable, BFormCheckbox, EditableUrl, WorkflowEmbed, ErrorMessages, UserSharing, Heading };
    }
});
